import { useController, useFormContext } from 'react-hook-form';
import React, { useCallback, useRef, useState } from 'react';
import MuiTextField from '@mui/material/TextField';
import { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField/TextField';
import _ from 'lodash';
import { Box, DialogContent, IconButton, InputAdornment } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { extractFirstTextFromHTML } from '@travelity/utils';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import LexicalEditorTopBar from '../rich-text-editor/components/rich-text-topbar';
import { LoadInitialContent } from '../rich-text-editor/components/load-initial';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { lexicalEditorConfig } from '../rich-text-editor';
import { OnChangePlugin } from '../rich-text-editor/plugins/on-change-plugin';
import { EditorState } from 'lexical';

export type RichTextAreaProps = MuiTextFieldProps & {
    name: string;
    label: string;
    required?: boolean;
    fullWidth?: boolean;
    hideHelperText?: boolean;
    showErrors?: boolean;
};

const RichTextArea: React.FC<RichTextAreaProps> = ({
    name,
    required,
    label,
    fullWidth,
    hideHelperText,
    showErrors,
    ...rest
}) => {
    const {
        control,
        watch,
        formState: { errors },
    } = useFormContext();
    const { field } = useController({ control, name });
    const inputRef = useRef<HTMLInputElement>();
    const buttonRef = useRef<HTMLButtonElement>(null);

    const error =
        showErrors && (_.get(errors, name)?.message as string | undefined);
    const [modalState, setStateModal] = useState({
        text: '',
        open: false,
    });

    const onModalSave = () => {
        field.onChange(modalState.text);
        field.onBlur();

        setStateModal({
            text: '',
            open: false,
        });
        setTimeout(() => {
            buttonRef.current?.focus();
        }, 100);
    };

    const onModalClose = (event?: Object, reason?: string) => {
        if (reason && reason === 'backdropClick') return;
        setStateModal({
            text: '',
            open: false,
        });
        setTimeout(() => {
            buttonRef.current?.focus();
        }, 100);
    };

    const value = watch(name);
    const onModalOpen = () => {
        setStateModal({
            text: value,
            open: true,
        });
    };

    const onTextFieldChange = useCallback(
        (e: { content: string; editorState: EditorState }) => {
            setStateModal(prevState => ({
                ...prevState,
                text: e.content,
            }));
        },
        []
    );

    const textValue = extractFirstTextFromHTML(field.value || '', 100);

    return (
        <>
            <MuiTextField
                inputRef={inputRef}
                fullWidth={false}
                margin="dense"
                data-testid={`text-area-${name}`}
                InputLabelProps={{
                    required: !!required,
                    shrink: !!rest.placeholder,
                }}
                label={label}
                value={textValue}
                type="text"
                error={!!error}
                helperText={!hideHelperText ? error : undefined}
                {...rest}
                sx={{
                    m: 0,
                    width: fullWidth ? '100%' : { xs: '100%', sm: '388px' },
                    '&&&& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #DFE1ED',
                    },
                    '&&&& .Mui-focused.MuiFormLabel-root': {
                        color: '#949BAC',
                    },
                    ...(rest.sx || {}),
                }}
                onClick={onModalOpen}
                inputProps={{
                    tabIndex: -1,
                }}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                ref={buttonRef}
                                onClick={onModalOpen}
                                edge="end"
                                sx={{
                                    border: '2px solid transparent',
                                    '&.Mui-focusVisible': {
                                        border: '2px solid #55B5CF',
                                    },
                                }}
                            >
                                <FullscreenIcon sx={{ color: '#B2B9CD' }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <Dialog
                open={modalState.open}
                onClose={onModalClose}
                disableRestoreFocus={true}
                sx={{
                    '& .MuiDialog-container': {
                        // justifyContent: 'end',
                        // alignItems: 'end',
                    },
                    '& .MuiDialog-paper': {
                        backgroundColor: '#FFF',
                        boxShadow: '0px 0px 12px 0px rgba(178, 185, 205, 0.50)',
                    },
                }}
                PaperProps={{
                    sx: {
                        width: '600px',
                        height: '400px',
                        m: 2,
                    },
                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle
                    sx={{ fontSize: '16px', fontWeight: 600 }}
                    id="alert-dialog-title"
                >
                    {label}
                </DialogTitle>
                <DialogContent
                    sx={{ pb: 0, overflow: 'hidden', overflowY: 'auto' }}
                >
                    <LexicalComposer initialConfig={lexicalEditorConfig}>
                        <LexicalEditorTopBar />

                        <Box
                            sx={{
                                border: '1px solid #DFE1ED',
                                borderRadius: '32px',
                                pb: 2,
                            }}
                        >
                            <LoadInitialContent initialContent={field.value} />
                            <RichTextPlugin
                                contentEditable={
                                    <Box
                                        component={ContentEditable}
                                        sx={{
                                            my: 1,
                                            mx: 3,
                                            minHeight: '120px',
                                            '&:focus': {
                                                outline: 'none',
                                            },
                                        }}
                                    />
                                }
                                ErrorBoundary={LexicalErrorBoundary}
                            />
                            <OnChangePlugin onChange={onTextFieldChange} />
                            <ListPlugin />
                            <LinkPlugin />
                        </Box>
                    </LexicalComposer>
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        variant="contained"
                        color="neutral"
                        onClick={onModalClose}
                    >
                        Discard
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={onModalSave}
                        disabled={required && !modalState.text}
                    >
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RichTextArea;
